import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { bankAcctOption } from '../billing/PaymentFormModal';
import { request } from '../../api/client';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { FileUploadField } from '../../components/FileUploadField';
import { useCanSeeWarehouses, usePermission } from '../../store/auth';
import { warehouseOption } from '../../lib/warehouseLabel';
import { TriangleAlert, ClipboardList, Package } from 'lucide-react';
import { Modal, Button, Select, Input, Textarea, InputNumber, Alert, message, EntityAvatar, StatCard, Spinner, type StatTone } from '../../ui';
import { inr2 } from '../../lib/format';
import { OrderStockLines } from '../orders/OrderStockLines';
import { ExpenseClaimReview, proofGapsOf } from '../expense/ExpenseClaimReview';
import { ClaimOutcomeSummary, computeClaimOutcome } from '../expense/ClaimMoneyPicture';
import { SettlementApproveDialog } from '../expense/SettlementApproveDialog';
import { PayReimbursementDialog } from '../expense/PayReimbursementDialog';
import { RecordRecoveryDialog } from '../expense/RecordRecoveryDialog';

/**
 * A SIGNED balance, said in words rather than left as a bare minus sign.
 *
 * "₹-404" in red told the reader the opposite of the truth: a negative dealer balance is money the
 * dealer has ALREADY paid, sitting in their favour. Owed is red, credit is green, settled is neutral,
 * and the sign is spelled out so nobody has to remember which way round the ledger runs.
 *
 * It lives here, beside the dialog that decides on the figure, and the queue row imports it — one
 * definition, so the row and the dialog can never phrase the same balance differently.
 */
export function balanceText(v: number, kind: 'balance' | 'wallet') {
  const amt = inr2(Math.abs(v));
  /* `amount` + `word` are the same sentence taken apart, for a tile that wants the figure big and
     its meaning underneath — never a second phrasing of it. A settled balance has no figure worth
     enlarging, so it carries the word alone. */
  if (Math.abs(v) < 0.005) return { text: 'settled', cls: 'text-subtle', valueCls: 'text-subtle', tone: 'neutral' as StatTone, amount: null as string | null, word: 'settled' };
  if (kind === 'balance') {
    return v > 0
      ? { text: `${amt} owed`, cls: 'font-medium text-danger-600', valueCls: 'text-danger-600', tone: 'danger' as StatTone, amount: amt as string | null, word: 'owed' }
      : { text: `${amt} in credit`, cls: 'font-medium text-brand-700 dark:text-brand-400', valueCls: 'text-brand-700 dark:text-brand-400', tone: 'brand' as StatTone, amount: amt as string | null, word: 'in credit' };
  }
  // Rep wallet: positive = company cash the rep still holds; negative = we owe them.
  return v > 0
    ? { text: `${amt} float held`, cls: 'font-medium text-warning-600 dark:text-harvest-300', valueCls: 'text-warning-600 dark:text-harvest-300', tone: 'warning' as StatTone, amount: amt as string | null, word: 'float held' }
    : { text: `${amt} reimbursement due`, cls: 'font-medium text-danger-600', valueCls: 'text-danger-600', tone: 'danger' as StatTone, amount: amt as string | null, word: 'reimbursement due' };
}
import { formatIst, formatIstDate } from '../../lib/time';

/** Mirrors the backend registry's `LaneActionField`. */
export interface LaneActionField {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'warehouse' | 'bankAccount' | 'paymentMode' | 'date' | 'choice' | 'file';
  required?: boolean;
  help?: string;
  /** For `choice`: the key in the row's `meta` holding this field's options, priced for that row. */
  optionsFrom?: string;
  defaultValue?: string;
  /** Per-row default: the key in the row's `meta` this field opens on. Beats `defaultValue`. */
  defaultFrom?: string;
  /** The field may not exceed this row `meta` figure — the server refuses above it. */
  maxFrom?: string;
  /** Only asked when another answer makes it a real question — see the registry. */
  showWhen?: { field?: string; meta?: string; equals?: string[]; notEquals?: string[] };
}

export interface ChoiceOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** The answer the numbers point to. Marked, never auto-submitted — the approver still chooses. */
  recommended?: boolean;
  /** Why it is recommended, in this row's terms. Falls back to a bare "Recommended". */
  recommendedNote?: string;
  /** Opens selected. Used where the record already carries an answer the approver is reviewing. */
  preselected?: boolean;
  help?: string;
}

export interface LaneAction {
  key: string;
  label: string;
  method: 'POST' | 'PATCH';
  url: string;
  danger?: boolean;
  note?: string;
  fields?: LaneActionField[];
  openInPlace?: boolean;
  body?: Record<string, unknown>;
  /** Hidden when the viewer is the person who raised the record (see the registry). */
  hideWhenSelfRaised?: boolean;
  /** Statuses the record must be in for this action to be legal. Absent = any (see the registry). */
  whenStatus?: string[];
  /**
   * The record's own LINES, rendered live inside this dialog — see the registry's `preview`.
   *
   * `watch` names the field the panel re-reads against (the warehouse being picked) and `replaces`
   * the detail labels it says better, which the fact grid then drops instead of printing twice.
   */
  preview?: { kind: 'orderStockPlan'; watch?: string; replaces?: string[] };
  /** Hidden from whoever took the previous step on this record; `unlessRole` is the domain's exemption. */
  hideWhenSelfDecided?: { unlessRole?: string };
  /** Only offered when the row's `meta` says the action applies — see `actionApplies`. */
  showWhen?: { meta: string; equals?: string[]; notEquals?: string[] };
}

/** Wallet payment modes — the set `LedgerService.postWith` accepts (NOT the billing mode list). */
const WALLET_MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER'];

/**
 * The one decision dialog for every lane.
 *
 * It is built entirely from the action descriptor the API returns, which is what keeps the hub
 * honest: the fields shown here are the fields the domain's own endpoint requires, because both
 * come from the same registry. Adding a required field to a decision on its own screen and
 * forgetting it here is not possible — there is no second list to forget.
 *
 * The call itself goes to the DOMAIN's endpoint, so its permission check, row scope, audit entry
 * and notifications all happen exactly as they do from that domain's own page.
 */
function GenericDecisionModal({
  open, onClose, action, item, evidence, facts, onDone,
}: {
  open: boolean;
  onClose: () => void;
  action: LaneAction | null;
  item: { id: string; docNo?: string | null; title?: string; domain: string; meta?: Record<string, any> } | null;
  /**
   * The evidence behind the record, shown INSIDE the decision dialog.
   *
   * The expanded row already carries it, but the dialog covers the row — so at the exact moment
   * somebody presses "Confirm in bank", the slip they are confirming was off screen. Repeating it
   * here costs one thumbnail strip and removes the only step where looking was optional.
   */
  evidence?: { label: string; files: string[] }[];
  /**
   * The record's own facts, shown at the moment of decision.
   *
   * The expanded row carries them — and then the dialog covers the row. So at the exact instant
   * somebody presses "Confirm in bank", the amount, the account, the depositor and the collections it
   * covers were all off screen, and the only thing left in front of them was a slip and a Note box.
   * Repeating them here is the difference between deciding and guessing.
   */
  facts?: Array<{ label: string; value: string | number | null; kind?: string }>;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, any>>({});
  const [touched, setTouched] = useState(false);

  const fields = action?.fields ?? [];
  const needsWarehouses = fields.some((f) => f.type === 'warehouse');
  const needsBanks = fields.some((f) => f.type === 'bankAccount');
  const canSeeWarehouses = useCanSeeWarehouses();

  // Reset on every open so a previous decision's text can never be submitted against a new record.
  useEffect(() => {
    if (!open) return;
    /* Seed any field that declares a default, so a required choice is never blank on open and the
       approver's first click is a decision rather than a correction. */
    const seed: Record<string, any> = {};
    for (const f of action?.fields ?? []) {
      if (f.defaultValue !== undefined) seed[f.name] = f.defaultValue;
      /* A per-row default beats the lane's static one: which pot a claim was filed against differs for
         every record, and opening on the wrong one would invite the approver to confirm a change they
         never meant to make. */
      if (f.type === 'choice' && f.optionsFrom) {
        const opts: ChoiceOption[] = item?.meta?.[f.optionsFrom] ?? [];
        const pre = opts.find((o) => o.preselected);
        if (pre) seed[f.name] = pre.value;
      }
      /* The same idea for a figure: "how much are we paying out" opens on what the record says is
         due, so the payer confirms a number rather than looking one up and retyping it. */
      if (f.defaultFrom && item?.meta?.[f.defaultFrom] !== undefined && item?.meta?.[f.defaultFrom] !== null) {
        seed[f.name] = item.meta[f.defaultFrom];
      }
    }
    setValues(seed);
    setTouched(false);
  }, [open, action?.key, item?.id]);

  const { data: warehouses } = useQuery({
    queryKey: ['approvals-warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    enabled: open && needsWarehouses && canSeeWarehouses,
  });
  const { data: banks } = useQuery({
    queryKey: ['approvals-bank-accounts'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
    enabled: open && needsBanks,
  });
  /* APPROVING AN ORDER AT A WAREHOUSE WHOSE GST CANNOT BE DETERMINED can only fail: the server re-determines GST inside
     the approval and refuses (GST_DETERMINATION_BLOCKED), after the Yes/No. The order page's dialog reads the same
     determination as a preview and holds its buttons back (DEF-069); this dialog decides the same order and never
     asked (DEF-073, 15 Sep). Same endpoint, same permission gate, same rule. */
  const canSeeGst = usePermission('GST.DETERMINATION.VIEW');
  const gstWarehouse = item?.domain === 'order' && action?.key === 'approve' ? values.warehouseId : undefined;
  const { data: gstPreview } = useQuery({
    queryKey: ['gst-determination-preview', String(item?.id ?? ''), gstWarehouse],
    queryFn: () => request<any>({ url: `/gst/orders/${item?.id}/determination`, params: { warehouseId: gstWarehouse } }),
    enabled: open && canSeeGst && !!item?.id && !!gstWarehouse,
    retry: false,
  });
  const gstBlocked = !!gstWarehouse && !!gstPreview?.engineEnabled && !gstPreview?.taxNature;

  /*
   * ONLY THE QUESTIONS THIS DECISION ACTUALLY RAISES.
   *
   * A disbursement paid in CASH was still shown "From bank account", starred and captioned "Required
   * for every non-cash mode" — asking which company account a fistful of notes came out of, and
   * saying in the same breath that it did not apply. The ledger discards the answer for cash, so the
   * only thing the picker could do was confuse the payer or stop them.
   *
   * A field's `showWhen` names what makes it relevant: another answer on this form, or a fact the row
   * already carries. Unanswered watched value = hidden, because relevance is not yet decidable.
   */
  const applies = (f: LaneActionField) => {
    const c = f.showWhen;
    if (!c) return true;
    const raw = c.field ? values[c.field] : c.meta ? item?.meta?.[c.meta] : undefined;
    const v = raw === undefined || raw === null ? '' : String(raw);
    if (!v) return false;
    if (c.equals && !c.equals.includes(v)) return false;
    if (c.notEquals && c.notEquals.includes(v)) return false;
    return true;
  };
  const shownFields = useMemo(
    () => fields.filter(applies),
    [fields, values, item?.meta],
  );

  // `required` means required WHEN ASKED. A hidden field is not an unanswered one.
  const missing = useMemo(
    () => shownFields.filter((f) => f.required && (values[f.name] === undefined || String(values[f.name]).trim() === '')),
    [shownFields, values],
  );

  const run = useConfirmedMutation({ title: action?.label ? `${action.label}?` : "Record this decision?", content: "It is recorded against your name in the approval trail.", okText: "Yes, continue" }, {
    mutationFn: () => request({
      url: String(action?.url ?? '').replace('{id}', String(item?.id ?? '')),
      method: action?.method ?? 'POST',
      // Only the fields actually ASKED are sent: switching the mode to CASH must not still post the
      // bank account picked a moment earlier under UPI, which the row would then be tagged with.
      // The action's own `body` (e.g. the review endpoint's approve flag) is merged UNDER the typed
      // values so a field can never accidentally override the decision the button represents.
      data: {
        ...Object.fromEntries(shownFields.filter((f) => values[f.name] !== undefined).map((f) => [f.name, values[f.name]])),
        ...(action?.body ?? {}),
      },
    }),
    onSuccess: () => {
      message.success(`${action?.label} — done`);
      qc.invalidateQueries({ queryKey: ['approvals'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onDone();
      onClose();
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? e?.message ?? 'That did not go through'),
  });

  const set = (name: string, v: any) => setValues((s) => ({ ...s, [name]: v }));

  /*
   * A Select's "nothing chosen yet" value is UNDEFINED, never an empty string.
   *
   * The kit reads `value == null` as unset, and an empty string is not null — so every picker in this
   * dialog was drawn as though it already held a value: no placeholder, and no label to draw for the
   * value either, leaving a bare box with a chevron in it. The approver was being asked which bank
   * the money landed in by a control that said nothing at all. (The kit is right to keep `''` a real
   * value: filters across the app use it for "All severities", "All categories".)
   */
  const selectValue = (name: string) => {
    const v = values[name];
    return v === undefined || v === null || v === '' ? undefined : v;
  };

  const renderField = (f: LaneActionField) => {
    const invalid = touched && f.required && (values[f.name] === undefined || String(values[f.name]).trim() === '');
    const common = { id: `field-${f.name}` };
    let control: React.ReactNode;
    switch (f.type) {
      case 'textarea':
        control = <Textarea {...common} rows={3} value={values[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)} placeholder={f.help} />;
        break;
      case 'number':
        control = <InputNumber {...common} min={0} value={values[f.name] ?? undefined} onChange={(v) => set(f.name, v)} className="w-full" />;
        break;
      case 'date':
        control = <Input {...common} type="date" value={values[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)} />;
        break;
      case 'warehouse':
        control = (
          <Select
            {...common}
            className="w-full"
            value={selectValue(f.name)}
            onChange={(v) => set(f.name, v)}
            placeholder="Select a warehouse"
            options={(warehouses ?? []).map((w: any) => ({
              value: String(w.id), ...warehouseOption(w),
            }))}
          />
        );
        if (gstBlocked && values[f.name] === gstWarehouse) {
          control = (
            <>
              {control}
              <Alert type="warning" className="mt-2"
                title="GST cannot be determined for this warehouse"
                description={`${(gstPreview?.blockers ?? []).map((b: any) => b.message).join(' ') || 'The supplying registration is not established.'} This order cannot be approved from here — pick another warehouse, or have this one mapped to a GST registration first.`} />
            </>
          );
        }
        break;
      /*
       * The SAME option renderer every other bank picker in the app uses - name, last four digits
       * and a green "Primary" chip.
       *
       * This case used to build its own label out of `accountNumber`, a field `/company/bank-accounts/options`
       * does not return (it returns `last4`, deliberately - the full number is never sent to a
       * picker). So the join dropped it and every option collapsed to the bank's NAME alone: two
       * accounts at the same bank were indistinguishable, and the one the company actually banks
       * through was not marked. An approver was being asked which account the money landed in and
       * shown nothing that could answer it.
       */
      case 'bankAccount':
        control = (
          <Select
            {...common}
            className="w-full"
            value={selectValue(f.name)}
            onChange={(v) => set(f.name, v)}
            placeholder="Select a bank account"
            options={(banks ?? []).map(bankAcctOption)}
          />
        );
        break;
      /*
       * A DECISION WITH A PRICE ON EACH ANSWER, not a dropdown.
       *
       * "Where should this come from" is the difference between paying ₹5,000 out and paying nothing,
       * and the labels carry the live figures that decide it. A collapsed Select hides both answers
       * behind one line and makes the cheaper one something you have to go looking for; laid out as
       * cards, the consequence of each is readable before the approver commits to either.
       *
       * An option the numbers rule out is DISABLED rather than hidden — "they are not carrying any"
       * is itself the answer to why the other choice is the only one, and hiding it would leave the
       * approver wondering whether the option exists at all.
       */
      case 'choice': {
        const options: ChoiceOption[] = (f.optionsFrom ? item?.meta?.[f.optionsFrom] : null) ?? [];
        control = (
          <div className="flex flex-col gap-2">
            {options.map((o) => {
              const picked = values[f.name] === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  disabled={o.disabled}
                  onClick={() => set(f.name, o.value)}
                  className={`flex w-full items-start gap-2.5 rounded-control border px-3 py-2 text-left transition-colors ${
                    o.disabled
                      ? 'cursor-not-allowed border-line bg-surface-2/60 opacity-60'
                      : picked
                        ? 'border-brand-500 bg-brand-500/10'
                        : 'border-line bg-surface hover:border-brand-400'
                  }`}
                >
                  <span className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border ${
                    picked ? 'border-brand-600 bg-brand-600' : 'border-line bg-surface'
                  }`}>
                    {/* ui-ok: the dot sits on an always-brand-600 fill, so it stays white in both themes. */}
                    {picked && <span className="size-1.5 rounded-full bg-white" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block type-body text-content">{o.label}</span>
                    {/*
                      * WHY it is recommended, in the option's own words.
                      *
                      * This chip used to read "Costs the company nothing" for every recommended
                      * option, which was true while the only one ever recommended was "they already
                      * hold the whole amount". A part-cash answer is recommended too and costs the
                      * company the shortfall — the chip said the opposite of the figure printed
                      * directly above it. The reason now comes from the row, beside the pricing that
                      * produced it, and a bare "Recommended" is the honest fallback.
                      */}
                    {o.recommended && !o.disabled && (
                      <span className="mt-0.5 inline-block type-caption font-medium text-success-600 dark:text-success-500">
                        {o.recommendedNote ?? 'Recommended'}
                      </span>
                    )}
                    {o.help && <span className="mt-0.5 block type-caption text-subtle">{o.help}</span>}
                  </span>
                </button>
              );
            })}
            {!options.length && (
              <p className="type-caption text-subtle">No options available for this record.</p>
            )}
          </div>
        );
        break;
      }
      case 'paymentMode':
        control = (
          <Select {...common} className="w-full" value={selectValue(f.name)} onChange={(v) => set(f.name, v)} placeholder="How was it paid?"
            options={WALLET_MODES.map((m) => ({ value: m, label: m.replace(/_/g, ' ') }))} />
        );
        break;
      /*
       * EVIDENCE, ASKED FOR IN THE HUB.
       *
       * The dialog could render every kind of answer except a file, so any lane that needed proof had
       * to go without it — which is how a non-cash advance payout came to be refused without a receipt
       * on the Wallets screen and accepted without one here, for the same act on the same endpoint.
       *
       * The same uploader the domain screens use, so a receipt attached here is stored, named and
       * previewed exactly like one attached there, and the value posted is the URL the field holds.
       */
      case 'file':
        control = (
          <FileUploadField
            category="receipt"
            value={values[f.name] ?? undefined}
            onChange={(url) => set(f.name, url)}
            buttonLabel={values[f.name] ? 'Replace file' : 'Upload receipt / screenshot'}
          />
        );
        break;
      default:
        control = <Input {...common} value={values[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)} placeholder={f.help} />;
    }
    return (
      <div key={f.name} className="mb-3.5 last:mb-0">
        <label htmlFor={`field-${f.name}`} className="mb-1 block type-label text-content">
          {f.label}{f.required ? <span className="ml-0.5 text-danger-600">*</span> : null}
        </label>
        {control}
        {f.help && f.type !== 'text' && f.type !== 'textarea' ? <p className="mt-1 type-caption text-subtle">{f.help}</p> : null}
        {invalid ? <p className="mt-1 type-caption text-danger-600">{f.label} is required.</p> : null}
        {overCeiling === f ? <p className="mt-1 type-caption text-danger-600">Can’t be more than {inr2(item?.meta?.[f.maxFrom!])} — the amount claimed.</p> : null}
      </div>
    );
  };

  /*
   * THE RECORD AND THE FLAGS ARE TWO DIFFERENT KINDS OF THING, so they are no longer interleaved.
   *
   * Every fact used to go into one two-column grid, warnings included - and a warning is a sentence,
   * not a value. Three lines of amber set the height of its whole grid row, so the one-line fact
   * beside it ("Collected cash they hold - RS 1,466") sat marooned at the top of a column of blank
   * surface, and the dialog grew a scrollbar to show mostly emptiness. Split, each reads as what it
   * is: the record is a dense grid of values, the flags are full-width callouts beneath it.
   */
  /*
   * A LINE THE PANEL SAYS BETTER IS NOT SAID TWICE.
   *
   * The order lane's 'Contents · AGN × 1' is a one-line stand-in for the very table the preview panel
   * below now draws in full. Left in the grid it would be the same fact in weaker words, three inches
   * above the strong one. The LANE names what its panel supersedes (`preview.replaces`) — the dialog
   * does not guess.
   */
  const superseded = new Set((action?.preview?.replaces ?? []).map((l) => l.toLowerCase()));
  const kept = (facts ?? []).filter((f) => !superseded.has(String(f.label).trim().toLowerCase()));
  const dataFacts = kept.filter((f) => f.kind !== 'warn');
  const flagFacts = kept.filter((f) => f.kind === 'warn');

  /*
   * THE FACTS ARE NOT ALL THE SAME KIND OF FACT, so they no longer all look the same.
   *
   * Ten label/value pairs in one grey grid is a form, not a briefing: the ₹300 the dealer already
   * paid, the ₹7,00,000 they are allowed to owe and the word "WEB" were set in identical type, and
   * the approver had to read all ten to find the two that decide the answer. Three tiers now:
   *
   *   1. WHO it is about — the fact that names the subject, promoted to a header with its identity
   *      mark, so the dialog opens with a dealer rather than with a field called "Dealer".
   *   2. THE MONEY — every figure as a tinted KPI tile in the app's standard StatCard, so the
   *      numbers are read as numbers and a signed balance keeps its own colour (owed red, credit
   *      green, float amber).
   *   3. EVERYTHING ELSE — the remaining context, still a label/value grid, where it belongs.
   */
  const MONEY_KINDS = new Set(['money', 'balance', 'wallet']);
  const moneyFacts = dataFacts.filter((f) => MONEY_KINDS.has(String(f.kind)));
  const otherFacts = dataFacts.filter((f) => !MONEY_KINDS.has(String(f.kind)));
  /*
   * The subject fact is found, not hard-coded: every lane names its subject differently ("Dealer",
   * "Staff", "Raised by"), but the queue row's own title IS that subject's name, so the fact whose
   * value contains it is the one that introduces the record. Promoting it — rather than printing the
   * title separately — keeps the code beside the name ("Dealer · LPBC_04") and repeats nothing.
   */
  const subjectName = String(item?.title ?? '').trim();
  const subjectFact = subjectName.length >= 3
    ? otherFacts.find((f) => typeof f.value === 'string' && f.value.includes(subjectName))
    : undefined;
  const subjectMeta = subjectFact
    ? String(subjectFact.value).replace(subjectName, '').replace(/^[\s·,–—-]+|[\s·,–—-]+$/g, '')
    : '';
  const gridFacts = subjectFact ? otherFacts.filter((f) => f !== subjectFact) : otherFacts;
  /*
   * A MISSING THING IS NOT AN ORDINARY VALUE. "No items on this order" under an order somebody is
   * about to approve, or "none given" where a discount reason should be, is the single most
   * decision-relevant line in the grid and it was set in the same grey as the channel. Amber names
   * the gap without pretending to be one of the lane's own warnings (those are the callouts below).
   */
  const isGap = (f: { value: any; kind?: string }) => {
    if (MONEY_KINDS.has(String(f.kind)) || f.kind === 'date') return false;
    const v = String(f.value ?? '').trim();
    // `\b` keeps "Nokia" and "Nothing"-shaped words apart from "No stock" / "not given"; a bare dash
    // has no word boundary at all, so it is matched on its own.
    return /^(no|none|not|never|nothing|n\/a)\b/i.test(v) || /^[—–-]$/.test(v);
  };
  const shownEvidence = (evidence ?? []).filter((e) => e.files?.length);
  /*
   * AN EXPENSE CLAIM IS DECIDED ON THE WHOLE CLAIM.
   *
   * The lane's facts said "What is claimed · TRAVEL ₹192" and "Bills attached 0 of 1" — a summary of a
   * summary. The approver needs every expense with its vehicle, rate and proof, and the rep's float and
   * collection cash behind it: the same review the register's Review & approve draws
   * (`ExpenseClaimReview`), which replaces the generic facts here. Its money picture follows the amount
   * and the pot being chosen in the form beside it.
   */
  const expenseDomain = item?.domain === 'expense_report' || item?.domain === 'expense_settlement';
  const { data: claimReport, isLoading: claimLoading } = useQuery({
    queryKey: ['expense-report', String(item?.id ?? '')],
    queryFn: () => request<any>({ url: `/expense-reports/${item?.id}` }),
    enabled: open && expenseDomain && !!item?.id,
    refetchOnMount: 'always',
  });
  // Approving with required proof missing is refused by the server — do not offer the button.
  // A figure above its ceiling (an approved amount above the claim) is refused by the server — block it here.
  const overCeiling = (action?.fields ?? []).find((f) => f.maxFrom && item?.meta?.[f.maxFrom] != null
    && values[f.name] != null && String(values[f.name]).trim() !== '' && Number(values[f.name]) > Number(item.meta![f.maxFrom]) + 0.005);
  const numOrNull = (v: any) => (v === undefined || v === null || String(v).trim() === '' ? null : Number(v));
  // On an expense approval, an amount/pot combination the server would refuse (more cash than they hold,
  // a split with no cash part) is caught by the same calculation the outcome box shows.
  const outcomeProblem = item?.domain === 'expense_report' && action?.key === 'approve' && claimReport?.moneyPicture
    ? computeClaimOutcome(claimReport.moneyPicture, { amount: numOrNull(values.approvedAmount), fundingSource: values.fundingSource, cashPart: numOrNull(values.cashFundedAmount) }).problem
    : null;
  const proofBlocked = item?.domain === 'expense_report' && action?.key === 'approve' && !!claimReport && proofGapsOf(claimReport).length > 0;
  /*
   * THE RECORD'S LINES, live, at the moment of decision.
   *
   * An order reached this dialog as a dealer, a total and a warehouse picker: what was actually being
   * ordered, how much of it, and whether the warehouse being chosen holds any were all on ANOTHER
   * screen. It is the same panel that screen draws, watching the field the lane names — so the picker
   * and the stock position answer each other here, as they do there.
   */
  const preview = action?.preview;
  const previewWarehouse = preview?.watch ? values[preview.watch] : undefined;
  const showPreview = !!preview && !!item?.id;
  const hasContext = expenseDomain || !!(dataFacts.length || flagFacts.length || shownEvidence.length || showPreview);
  /*
   * SIDE BY SIDE, so the dialog's height is the taller half rather than the sum of both.
   *
   * What is being decided and how you decide it were stacked, which is why disbursing an advance
   * scrolled: roughly 450px of context above 250px of form, in a dialog that can only be about 700px
   * tall. The approver scrolled past the evidence to reach the fields and then could no longer see
   * the evidence. Beside each other, both are on screen at once and neither moves.
   *
   * Only when there is something in BOTH halves - a bare confirmation has no second column to fill,
   * and a lane with no facts would leave the form stranded in a narrow strip.
   */
  const twoPane = hasContext && shownFields.length > 0;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      /* Sized to the layout: two panes need room for a two-column fact grid AND the form beside it;
         a facts-only dialog keeps the 720px that grid was widened to; a bare confirmation stays
         small rather than opening a near-empty sheet. */
      width={expenseDomain ? 1180 : twoPane ? 960 : hasContext ? 720 : 520}
      title={`${action?.label ?? 'Decide'} \u00b7 ${item?.docNo ?? item?.title ?? ''}`}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            variant={action?.danger ? 'danger' : 'primary'}
            loading={run.isPending}
            disabled={proofBlocked || !!overCeiling || !!outcomeProblem || gstBlocked}
            onClick={() => { setTouched(true); if (!missing.length && !proofBlocked && !overCeiling && !outcomeProblem && !gstBlocked) run.mutate(); }}
          >
            {action?.label ?? 'Confirm'}
          </Button>
        </>
      }
    >
      {action?.note ? <Alert type="info" message={action.note} className="mb-4" /> : null}

      {/* Row 1 puts the record beside the form; row 2 runs the full width underneath. Flags laid out
          in the narrow left column instead would tower over a short form and leave a column of dead
          space beside it — and a warning is a sentence, which reads better long than tall. */}
      <div className={twoPane
        // An expense decision carries its outcome box in the form column — give it room to read in whole lines.
        ? `grid items-start gap-x-5 gap-y-3 ${expenseDomain ? 'lg:grid-cols-[minmax(0,1fr)_440px]' : 'lg:grid-cols-[minmax(0,1fr)_360px]'}`
        : 'space-y-3'}>
        {/*
          * WHAT IS BEING DECIDED, before how to decide it.
          *
          * The expanded row carries these facts - and then this dialog covers the row. So at the
          * moment somebody pressed "Confirm in bank", the amount, the account, the depositor and the
          * collections it covers were all off screen, and the only things left were a slip and a Note
          * box. They come with the dialog now.
          *
          * Label ABOVE value, never beside it: side by side the value gets whatever the label leaves,
          * and a money figure broke across two lines as "RS 3,11 / 6" - to be reassembled by eye, on
          * the screen where somebody decides whether to release that money.
          *
          * NO SCROLLER OF ITS OWN. This grid was once capped at 15rem with its own overflow, which
          * put a second scrollbar inside a dialog body that already scrolls. The facts are the point
          * of the dialog, so they all render; if a long list still overflows, the body scrolls as one
          * thing.
          */}
        {expenseDomain && (
          <div className={twoPane ? 'min-w-0 lg:col-start-1 lg:row-start-1' : 'min-w-0'}>
            <section className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
              <h3 className="flex items-center gap-1.5 border-b border-line/70 bg-surface-2/60 px-3.5 py-2 type-overline text-subtle">
                <ClipboardList className="size-3.5" aria-hidden />
                What you are deciding
              </h3>
              <div className="p-3">
                {claimReport ? (
                  <ExpenseClaimReview
                    report={claimReport}
                    mode={item?.domain === 'expense_settlement' ? 'pay' : 'approve'}
                    amount={numOrNull(values.approvedAmount)}
                    fundingSource={values.fundingSource ?? null}
                    cashPart={numOrNull(values.cashFundedAmount)}
                  />
                ) : claimLoading ? (
                  <div className="flex items-center justify-center gap-2 py-8 text-muted"><Spinner /> Loading the claim…</div>
                ) : (
                  <div className="py-6 text-center text-sm text-danger-600 dark:text-danger-500">Couldn’t load this claim.</div>
                )}
              </div>
            </section>
          </div>
        )}
        {!expenseDomain && !!dataFacts.length && (
          <div className={twoPane ? 'min-w-0 lg:col-start-1 lg:row-start-1' : 'min-w-0'}>
            <section className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
              <h3 className="flex items-center gap-1.5 border-b border-line/70 bg-surface-2/60 px-3.5 py-2 type-overline text-subtle">
                <ClipboardList className="size-3.5" aria-hidden />
                What you are deciding
              </h3>

              {/* WHO IT IS ABOUT. The identity mark's hue is a function of the name, so the same
                  dealer is the same colour here as in every register that draws them — an anchor,
                  not decoration. */}
              {subjectFact && (
                <div className="flex items-center gap-3 border-b border-line/70 px-3.5 py-3">
                  <EntityAvatar name={subjectName} size={38} square />
                  <div className="min-w-0">
                    <div className="truncate type-body-strong text-content">{subjectName}</div>
                    <div className="truncate type-caption text-subtle">
                      {subjectFact.label}{subjectMeta ? ` · ${subjectMeta}` : ''}
                    </div>
                  </div>
                </div>
              )}

              {/* THE MONEY, as money. Same StatCard the dashboards use, so a figure in a decision
                  dialog is read the same way as a figure anywhere else in the app — and a signed
                  balance keeps the colour its sign earns. */}
              {!!moneyFacts.length && (
                /* Two figures get half the pane each, three or more share thirds: a lane with a
                   single pair ("Amount", "Depositor still accountable for") was truncating its own
                   labels inside a third-width tile while two thirds of the row sat empty. */
                <div className={`grid gap-2 border-b border-line/70 p-3 ${
                  moneyFacts.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
                  {moneyFacts.map((f, i) => {
                    const signed = f.kind === 'balance' || f.kind === 'wallet'
                      ? balanceText(Number(f.value), f.kind as 'balance' | 'wallet') : null;
                    return (
                      <StatCard
                        key={`${f.label}-money-${i}`}
                        tone={signed ? signed.tone : 'sky'}
                        /* The tile clips a long label to one line; `title` keeps the full wording
                           reachable rather than lost. */
                        label={<span title={f.label}>{f.label}</span>}
                        /* The SIGN keeps its colour on the figure itself, not only on the tile's
                           rail: owed is red, in credit is green, a float is amber, everywhere in the
                           app. The nested span beats StatCard's `text-content` on the value. */
                        value={signed
                          ? <span className={signed.valueCls}>{signed.amount ?? signed.word}</span>
                          : inr2(f.value)}
                        hint={signed && signed.amount ? signed.word : undefined}
                      />
                    );
                  })}
                </div>
              )}

              {/* THE REST OF THE RECORD. Each pair sits in its own soft card rather than floating in
                  a shared grid: equal boxes give the eye an edge to follow down two columns, and it
                  costs no per-item border logic that breaks on an odd number of facts. */}
              {!!gridFacts.length && (
              <dl className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
                {gridFacts.map((f, i) => {
                  /* Money and signed balances are tiles above; anything reaching this grid is
                     context. The one distinction left to draw is between a value and a GAP —
                     which is amber, because "No items on this order" is the line an approver most
                     needs to catch and it used to be the same grey as the channel. */
                  const gap = isGap(f);
                  return (
                    <div
                      key={`${f.label}-${i}`}
                      className={`min-w-0 rounded-control border px-2.5 py-1.5 ${
                        gap
                          ? 'border-warning-500/30 bg-warning-500/10'
                          : 'border-line/60 bg-surface-2/50'
                      }`}
                    >
                      <dt className="type-label text-subtle">{f.label}</dt>
                      <dd className={`mt-0.5 min-w-0 break-words type-body ${
                        gap ? 'font-medium text-warning-600 dark:text-harvest-300' : 'text-content'
                      }`}>
                        {/* A raw ISO instant is not a fact anybody can read - formatted exactly as
                            the row formats it, so the dialog and the row can never disagree. */}
                        {f.kind === 'date'
                          ? (/^\d{4}-\d{2}-\d{2}T/.test(String(f.value))
                              ? formatIst(String(f.value))
                              : formatIstDate(String(f.value)))
                          : String(f.value)}
                      </dd>
                    </div>
                  );
                })}
              </dl>
              )}
            </section>
          </div>
        )}

        {/* THE THINGS THAT SHOULD GIVE AN APPROVER PAUSE, said as sentences and marked as warnings -
            not as one more grey value in the grid, and full width so they read as prose. */}
        {!expenseDomain && !!(flagFacts.length || shownEvidence.length || showPreview) && (
          <div className={twoPane ? 'min-w-0 space-y-3 lg:col-span-2 lg:row-start-2' : 'min-w-0 space-y-3'}>
            {flagFacts.map((f, i) => {
              const text = String(f.value);
              /* A flag whose value is a FIGURE rather than a sentence - "Bills attached / 0 of 1" -
                 does not need a paragraph beneath its own heading. Stacked, three words drew a
                 two-line banner across the full width of the dialog. Said on one line, it is as
                 loud as it needs to be and no taller. */
              const terse = text.length <= 56 && !/[.!?]\s/.test(text);
              return (
                <div
                  key={`${f.label}-warn-${i}`}
                  className="flex gap-2.5 rounded-card border border-warning-500/25 bg-warning-500/10 px-3.5 py-2.5"
                >
                  <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning-600 dark:text-harvest-300" />
                  {terse ? (
                    <p className="min-w-0 break-words type-body text-content">
                      <span className="font-semibold text-warning-600 dark:text-harvest-300">{f.label}</span>
                      {' \u2014 '}{text}
                    </p>
                  ) : (
                    <div className="min-w-0">
                      <div className="type-label-strong text-warning-600 dark:text-harvest-300">{f.label}</div>
                      <p className="mt-0.5 break-words type-body text-content">{text}</p>
                    </div>
                  )}
                </div>
              );
            })}

            {shownEvidence.map((e) => (
              <div key={e.label} className="rounded-card border border-line bg-surface-2/40 px-3.5 py-2.5">
                <div className="mb-1.5 type-overline text-subtle">{e.label}</div>
                <AttachmentCell files={e.files} label={e.label} size={56} maxThumbs={6} />
              </div>
            ))}

            {/* WHAT IS BEING DECIDED ON, ITEM BY ITEM. Full width: the table carries up to five
                columns and the record pane is 360px short of holding them. */}
            {showPreview && (
              <section className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
                <h3 className="flex items-center gap-1.5 border-b border-line/70 bg-surface-2/60 px-3.5 py-2 type-overline text-subtle">
                  <Package className="size-3.5" aria-hidden />
                  What is on the order
                </h3>
                <div className="px-3.5 py-3">
                  <OrderStockLines orderId={String(item?.id)} warehouseId={previewWarehouse} />
                  {/* Only where a warehouse IS being picked. On Reject there is no picker, and the
                      panel is complete as it stands — telling that reader to pick one asks for
                      something this dialog does not offer. */}
                  {!!preview?.watch && !previewWarehouse && (
                    <p className="mt-2 type-caption text-subtle">
                      Pick a fulfilment warehouse to see what it holds and what it is already committed to.
                    </p>
                  )}
                </div>
              </section>
            )}
          </div>
        )}

        {/* HOW IT GETS DECIDED - tinted, so the half that takes input is the half that looks like it
            takes input, rather than a third grey card in a stack of grey cards. */}
        {shownFields.length ? (
          <div className={twoPane
            ? 'min-w-0 rounded-card border border-brand-500/25 bg-brand-500/5 px-3.5 py-3 lg:col-start-2 lg:row-start-1'
            : 'min-w-0'}>
            {twoPane && <h3 className="mb-3 type-overline text-subtle">Your decision</h3>}
            {(() => {
              /* WHAT THIS DECISION DOES, directly under the choices that make it and above Remarks — so the
                 amount and the pot can never be read as two sums that add together, and the answer is on
                 screen without scrolling. */
              const approving = item?.domain === 'expense_report' && action?.key === 'approve';
              const settling = item?.domain === 'expense_settlement' && action?.key === 'close';
              const showOutcome = (approving || settling) && !!claimReport?.moneyPicture;
              const lead = showOutcome ? shownFields.filter((f) => f.name !== 'remarks') : shownFields;
              const tail = showOutcome ? shownFields.filter((f) => f.name === 'remarks') : [];
              return (
                <>
                  {lead.map(renderField)}
                  {showOutcome && (
                    <div className="mb-3.5">
                      {settling ? (
                        <ClaimOutcomeSummary
                          verb="pay" picture={claimReport.moneyPicture} repName={claimReport.repName}
                          amount={Number(claimReport.paidAmount ?? claimReport.subtotalAmount ?? 0)} fundingSource={claimReport.fundingSource}
                          cashPart={Number(claimReport.cashFundedAmount ?? 0)}
                        />
                      ) : (
                        <ClaimOutcomeSummary
                          picture={claimReport.moneyPicture} repName={claimReport.repName}
                          amount={numOrNull(values.approvedAmount)} fundingSource={values.fundingSource} cashPart={numOrNull(values.cashFundedAmount)}
                        />
                      )}
                    </div>
                  )}
                  {tail.map(renderField)}
                </>
              );
            })()}
          </div>
        ) : (
          <p className="type-body text-muted">
            Confirm to {String(action?.label ?? '').toLowerCase()} {item?.docNo ? <b>{item.docNo}</b> : 'this record'}.
          </p>
        )}
      </div>

    </Modal>
  );
}

/**
 * EXPENSE CLAIMS ARE DECIDED IN THEIR OWN DIALOGS (settlement V2).
 *
 * Approving a claim is one number plus the server's settlement preview (wallet → collection → reimbursement),
 * and paying one is the frozen reimbursement plus bank, reference and proof — neither is a list of generic
 * fields. Every other lane keeps the registry-driven dialog above. Separate components, so switching between
 * them unmounts one and mounts the other and no hook order is ever shared.
 */
export function DecisionModal(props: Parameters<typeof GenericDecisionModal>[0]) {
  const { open, item, action, onClose, onDone } = props;
  if (open && item && item.domain === 'expense_report' && action?.key === 'approve') {
    return <SettlementApproveDialog reportId={String(item.id)} onClose={onClose} onDone={onDone} />;
  }
  if (open && item && item.domain === 'expense_settlement' && action?.key === 'close') {
    return <PayReimbursementDialog reportId={String(item.id)} onClose={onClose} onDone={onDone} />;
  }
  // Money owed back on a reversed, already-reimbursed claim: what was paid and why, then how it came back.
  if (open && item && item.domain === 'expense_recovery' && action?.key === 'receive') {
    return <RecordRecoveryDialog reportId={String(item.id)} onClose={onClose} onDone={onDone} />;
  }
  return <GenericDecisionModal {...props} />;
}
